import { Container, Graphics } from 'pixi.js'
import { NeonColorsHex } from '../ui/neon-theme'
import type { GameMode } from '../config/gameMode'
import { generateSnakeColors, mixColors, type SnakeColors } from './snakeColors'

export interface SnakeData {
  id: string
  x: number
  z: number
  bearing: number
  segments: { x: number; z: number }[]
  width: number
  segmentSpacing: number
  tailProgress: number
  isAlive: boolean
  isBoosting: boolean
  isInvulnerable: boolean
  isBot: boolean
  botBehavior?: number
  skinId: string
  name: string
}

interface PortalFadeEffect {
  id: string
  timer: number
  maxTimer: number
  snake: SnakeData
  colors: SnakeColors
}

interface DeathGhostEffect {
  id: string
  timer: number
  maxTimer: number
  snake: SnakeData
  colors: SnakeColors
}

interface WorldViewport {
  x: number
  z: number
  width: number
  height: number
}

/** Hysteresis distances to prevent style flicker near LOD boundary. */
const DETAIL_ENTER_DISTANCE = 2600
const DETAIL_EXIT_DISTANCE = 3200
const GLOW_ENTER_DISTANCE = 2200
const GLOW_EXIT_DISTANCE = 2800
/** Glow only for head-near segments to reduce overdraw. */
const MAX_GLOW_SEGMENTS = 96
const QUALITY_GLOW_CAP = [0, 12, 24, MAX_GLOW_SEGMENTS]
const PORTAL_FADE_DURATION = 0.55
const DEATH_GHOST_DURATION = 0.22
const RENDER_CULL_MARGIN = 900
const EFFECT_CULL_MARGIN = 700

interface BotPalette {
  head: number
  body: [number, number]
  glow: number
}

const BOT_PALETTES: Record<number, BotPalette> = {
  1:  { head: 0xF0E8E0, body: [0xE8E0D8, 0xE0D8D0], glow: 0x88BBEE },  // COLLECTING
  10: { head: 0x707070, body: [0x606060, 0x505050], glow: 0xCC5555 },  // HUNT approach
  11: { head: 0x707070, body: [0x606060, 0x505050], glow: 0xCC5555 },  // HUNT flank
  12: { head: 0x707070, body: [0x606060, 0x505050], glow: 0xCC5555 },  // HUNT feint
  13: { head: 0x707070, body: [0x606060, 0x505050], glow: 0xCC5555 },  // HUNT cross
  14: { head: 0x707070, body: [0x606060, 0x505050], glow: 0xCC5555 },  // HUNT encircle
  20: { head: 0xC0C0B0, body: [0xB0B0A0, 0xA8A898], glow: 0xBBAA66 },  // EVACUATING
}
const DEFAULT_BOT_PALETTE: BotPalette = BOT_PALETTES[1]!

interface BotColorLerpState {
  currentHead: number
  currentBody0: number
  currentBody1: number
  currentGlow: number
  targetHead: number
  targetBody0: number
  targetBody1: number
  targetGlow: number
  lastBehavior: number
}

export class SnakeRenderer {
  private container: Container
  private snakeContainers: Map<string, Container> = new Map()
  private snakeGraphics: Map<string, Graphics> = new Map()
  /** LOD state with hysteresis to avoid blinking between styles. */
  private detailState: Map<string, boolean> = new Map()
  private glowState: Map<string, boolean> = new Map()
  /** Override color seed for specific snake IDs (e.g. own snake uses menu colorSeed). */
  private colorSeedOverrides: Map<string, number> = new Map()
  private readonly colorCache: Map<number, SnakeColors> = new Map()
  private readonly portalFadeLayer: Container
  private readonly portalFadeEffects: Map<string, { effect: PortalFadeEffect; container: Container; gfx: Graphics }> = new Map()
  private readonly deathGhostLayer: Container
  private readonly deathGhostEffects: Map<string, { effect: DeathGhostEffect; container: Container; gfx: Graphics }> = new Map()
  private readonly activeIds: Set<string> = new Set()
  private lastPortalFadeUpdateMs = performance.now()
  private readonly botColorStates: Map<string, BotColorLerpState> = new Map()
  private qualityLevel: number = 2
  private renderMode: GameMode = 'modern'
  private bodyScale: number = 1

  constructor(parentContainer: Container) {
    this.container = new Container()
    parentContainer.addChild(this.container)
    this.portalFadeLayer = new Container()
    parentContainer.addChild(this.portalFadeLayer)
    this.deathGhostLayer = new Container()
    parentContainer.addChild(this.deathGhostLayer)
  }

  update(
    snakes: SnakeData[],
    cameraX: number,
    cameraZ: number,
    ownSnakeId: string | null,
    viewport?: WorldViewport,
  ): void {
    this.activeIds.clear()
    const nowPerf = performance.now()
    const fadeDt = Math.min(0.1, Math.max(0, (nowPerf - this.lastPortalFadeUpdateMs) / 1000))
    this.lastPortalFadeUpdateMs = nowPerf

    for (const snake of snakes) {
      if (!snake.isAlive) continue
      this.activeIds.add(snake.id)

      const isOwn = snake.id === ownSnakeId
      const renderable = isOwn || !viewport || this.isSnakeVisibleInViewport(snake, viewport, RENDER_CULL_MARGIN, snake.segments.length > 180 ? 4 : 2)
      if (!renderable) {
        const existingContainer = this.snakeContainers.get(snake.id)
        if (existingContainer) existingContainer.visible = false
        continue
      }

      const inDetailRange = this.isWithinLodBand(
        snake.id, snake.x, snake.z, cameraX, cameraZ,
        this.detailState, DETAIL_ENTER_DISTANCE, DETAIL_EXIT_DISTANCE,
      )
      const inGlowRange = this.isWithinLodBand(
        snake.id, snake.x, snake.z, cameraX, cameraZ,
        this.glowState, GLOW_ENTER_DISTANCE, GLOW_EXIT_DISTANCE,
      )
      const visibleOnScreen = viewport ? this.isSnakeVisibleInViewport(snake, viewport) : false
      const highDetail = isOwn || visibleOnScreen || (inDetailRange && this.qualityLevel > 0)
      const drawGlow = isOwn || (inGlowRange && this.qualityLevel > 1)

      this.renderSnake(snake, drawGlow, snake.isInvulnerable, nowPerf, highDetail)
    }

    // Remove snakes that are no longer alive/present
    for (const [id, container] of this.snakeContainers) {
      if (!this.activeIds.has(id)) {
        this.container.removeChild(container)
        container.destroy({ children: true })
        this.snakeContainers.delete(id)
        this.snakeGraphics.delete(id)
        this.detailState.delete(id)
        this.glowState.delete(id)
        this.colorSeedOverrides.delete(id)
        this.botColorStates.delete(id)
      }
    }

    this.updatePortalFadeEffects(fadeDt, nowPerf, viewport)
    this.updateDeathGhostEffects(fadeDt, nowPerf, viewport)
  }

  private isWithinLodBand(
    snakeId: string,
    snakeX: number,
    snakeZ: number,
    cameraX: number,
    cameraZ: number,
    stateMap: Map<string, boolean>,
    enterDist: number,
    exitDist: number,
  ): boolean {
    const prev = stateMap.get(snakeId) ?? false
    const dx = snakeX - cameraX
    const dz = snakeZ - cameraZ
    const dist2 = dx * dx + dz * dz
    const enter2 = enterDist * enterDist
    const exit2 = exitDist * exitDist
    const next = prev ? dist2 <= exit2 : dist2 <= enter2
    stateMap.set(snakeId, next)
    return next
  }

  private isSnakeVisibleInViewport(
    snake: SnakeData,
    viewport: WorldViewport,
    extraMargin = 0,
    sampleStepOverride?: number,
  ): boolean {
    const margin = Math.max(180, snake.width * this.bodyScale * 2.5) + extraMargin
    const minX = viewport.x - margin
    const minZ = viewport.z - margin
    const maxX = viewport.x + viewport.width + margin
    const maxZ = viewport.z + viewport.height + margin

    if (
      snake.x >= minX &&
      snake.x <= maxX &&
      snake.z >= minZ &&
      snake.z <= maxZ
    ) {
      return true
    }

    const sampleStep = sampleStepOverride ?? (snake.segments.length > 120 ? 2 : 1)
    for (let i = 0; i < snake.segments.length; i += sampleStep) {
      const seg = snake.segments[i]
      if (seg.x >= minX && seg.x <= maxX && seg.z >= minZ && seg.z <= maxZ) {
        return true
      }
    }

    return false
  }

  private renderSnake(
    snake: SnakeData,
    drawGlow: boolean,
    isInvulnerable: boolean,
    now: number,
    highDetail: boolean,
  ): void {
    let snakeContainer = this.snakeContainers.get(snake.id)
    let gfx = this.snakeGraphics.get(snake.id)

    if (!snakeContainer) {
      snakeContainer = new Container()
      gfx = new Graphics()
      snakeContainer.addChild(gfx)
      this.container.addChild(snakeContainer)
      this.snakeContainers.set(snake.id, snakeContainer)
      this.snakeGraphics.set(snake.id, gfx)
    }

    if (!gfx) return
    snakeContainer.visible = true
    gfx.clear()

    // Colors per snake: use override seed if set (own snake), otherwise entityId
    const colorSeed = this.colorSeedOverrides.get(snake.id) ?? (Number(snake.id) || 0)
    let colors = this.resolveSnakeColors(colorSeed)
    let botGlowColor: number | null = null

    // Bot behavior-based monochrome colors with smooth lerp
    if (snake.isBot && snake.botBehavior != null && snake.botBehavior > 0) {
      const palette = BOT_PALETTES[snake.botBehavior] ?? DEFAULT_BOT_PALETTE
      let state = this.botColorStates.get(snake.id)
      if (!state) {
        state = {
          currentHead: palette.head,
          currentBody0: palette.body[0],
          currentBody1: palette.body[1],
          currentGlow: palette.glow,
          targetHead: palette.head,
          targetBody0: palette.body[0],
          targetBody1: palette.body[1],
          targetGlow: palette.glow,
          lastBehavior: snake.botBehavior,
        }
        this.botColorStates.set(snake.id, state)
      }

      if (snake.botBehavior !== state.lastBehavior) {
        state.targetHead = palette.head
        state.targetBody0 = palette.body[0]
        state.targetBody1 = palette.body[1]
        state.targetGlow = palette.glow
        state.lastBehavior = snake.botBehavior
      }

      // Lerp each frame (~0.5 sec transition at 60fps: dt ~16ms, factor ~0.032)
      const dt = Math.min(0.1, Math.max(0, (now - this.lastPortalFadeUpdateMs) / 1000))
      const lerpFactor = Math.min(1, Math.max(dt, 1 / 60) * 2.0)
      state.currentHead = mixColors(state.currentHead, state.targetHead, lerpFactor)
      state.currentBody0 = mixColors(state.currentBody0, state.targetBody0, lerpFactor)
      state.currentBody1 = mixColors(state.currentBody1, state.targetBody1, lerpFactor)
      state.currentGlow = mixColors(state.currentGlow, state.targetGlow, lerpFactor)

      colors = { head: state.currentHead, body: [state.currentBody0, state.currentBody1] }
      botGlowColor = state.currentGlow
    }

    const segments = snake.segments
    const width = snake.width * this.bodyScale
    const segmentSpacing = snake.segmentSpacing * this.bodyScale
    const qualityIdx = Math.max(0, Math.min(QUALITY_GLOW_CAP.length - 1, this.qualityLevel))
    const segCount = segments.length
    const glowSegLimit = Math.min(segCount, QUALITY_GLOW_CAP[qualityIdx])
    const segmentStep = highDetail ? 1 : (this.qualityLevel >= 2 ? 2 : 3)

    // Pulsating glow alpha (subtle breathing)
    const glowAlpha = Math.sin(now * 0.004) * 0.04 + 0.08

    const headSize = this.renderMode === 'classic' ? width * 0.5 : width * 0.7
    const mixedBodyColor = mixColors(colors.body[0], colors.body[1], 0.5)
    const accentBodyColor = mixColors(colors.body[0], colors.body[1], 0.22)

    // Bot aura: pulsating double-ring behind the head (drawn first, behind everything)
    if (snake.isBot && highDetail) {
      const glowCol = botGlowColor ?? colors.head
      const auraRadius = headSize * 2.5
      const auraAlpha = 0.12 + Math.sin(now * 0.004) * 0.08
      gfx.circle(snake.x, snake.z, auraRadius)
      gfx.fill({ color: glowCol, alpha: auraAlpha })
      gfx.circle(snake.x, snake.z, auraRadius * 0.7)
      gfx.fill({ color: glowCol, alpha: auraAlpha * 0.6 })
    }

    if (!highDetail) {
      if (segCount >= 2) {
        gfx.moveTo(segments[0].x, segments[0].z)
        for (let i = 1; i < segCount; i++) {
          const seg = segments[i]
          gfx.lineTo(seg.x, seg.z)
        }
        gfx.stroke({
          color: mixedBodyColor,
          alpha: 0.96,
          width: width * 0.82,
          cap: 'round',
          join: 'round',
        })
        gfx.moveTo(segments[0].x, segments[0].z)
        for (let i = 1; i < segCount; i++) {
          const seg = segments[i]
          gfx.lineTo(seg.x, seg.z)
        }
        gfx.stroke({
          color: accentBodyColor,
          alpha: 0.45,
          width: width * 0.42,
          cap: 'round',
          join: 'round',
        })
      }

      if (snake.tailProgress > 0.001 && segCount >= 1) {
        this.drawTailTip(gfx, snake, width, segmentSpacing, mixedBodyColor, 0.96)
      }
    } else {
      // Draw a continuous tube along segment centers to remove visible gaps
      // between circles at any quality level.
      if (segCount >= 2) {
        gfx.moveTo(segments[0].x, segments[0].z)
        for (let i = segmentStep; i < segCount; i += segmentStep) {
          const seg = segments[i]
          gfx.lineTo(seg.x, seg.z)
        }
        const tubeWidth = width * 0.68
        gfx.stroke({
          color: mixedBodyColor,
          alpha: 0.92,
          width: tubeWidth,
          cap: 'round',
          join: 'round',
        })
      }

      // Draw body segments (back to front) with optional glow layer
      for (let i = segCount - 1; i >= 0; i -= segmentStep) {
        const seg = segments[i]
        const t = i / Math.max(1, segCount - 1)
        const colorIdx = i % colors.body.length
        const segWidth = this.renderMode === 'classic'
          ? width
          : width * (0.6 + 0.4 * (1 - t))
        const radius = segWidth / 2

        // Glow layer (behind body, only for nearby snakes)
        if (drawGlow && i < glowSegLimit) {
          gfx.circle(seg.x, seg.z, radius * 1.5)
          gfx.fill({ color: colors.head, alpha: glowAlpha })
        }

        // Body segment
        gfx.circle(seg.x, seg.z, radius)
        gfx.fill({ color: colors.body[colorIdx], alpha: 0.94 })
      }

      if (snake.tailProgress > 0.001 && segCount >= 1) {
        if (drawGlow) {
          this.drawTailTip(gfx, snake, width * 1.05, segmentSpacing, colors.head, glowAlpha * 0.9)
        }
        this.drawTailTip(
          gfx,
          snake,
          width,
          segmentSpacing,
          colors.body[(segCount - 1) % colors.body.length],
          0.94,
        )
      }
    }

    if (this.renderMode === 'classic') {
      // Classic style: no oversized separate head, uniform width body.
      gfx.circle(snake.x, snake.z, headSize)
      gfx.fill({ color: mixColors(colors.body[0], colors.body[1], 0.45), alpha: 0.96 })
    } else {
      // Draw head
      gfx.circle(snake.x, snake.z, headSize)
      gfx.fill({ color: colors.head })
    }

    // Invulnerability: semi-transparent + blink to full invisibility (all snakes, not just highDetail)
    if (isInvulnerable) {
      // Blink: oscillate between semi-transparent (0.45) and near-invisible (0.08)
      // ~3 Hz blink rate (fast enough to be obvious)
      const blink = Math.sin(now * 0.019) // ~3 Hz
      snakeContainer.alpha = blink > 0 ? 0.45 : 0.08

      // White shimmer halo on head (only highDetail)
      if (highDetail) {
        const shimmerAlpha = 0.3 + Math.sin(now * 0.012) * 0.2
        gfx.circle(snake.x, snake.z, headSize * 1.6)
        gfx.fill({ color: NeonColorsHex.textBright, alpha: shimmerAlpha })
      }
    } else {
      snakeContainer.alpha = 1
    }

    // Draw eyes only in modern style (classic keeps a uniform body silhouette).
    if (highDetail && this.renderMode !== 'classic') {
      const eyeColor = (snake.isBot && botGlowColor != null) ? botGlowColor : 0xffffff
      this.drawEyes(gfx, snake, headSize, eyeColor)
    }

    // Bot diamond marker removed — bots identified by color only
  }

  private drawEyes(gfx: Graphics, snake: SnakeData, headSize: number, eyeColor: number = 0xffffff): void {
    const eyeOffset = headSize * 0.4
    const eyeSize = headSize * 0.25

    // Derive eye direction from body geometry (segment[1] → segment[0])
    // instead of raw bearing to avoid jittery eye rotation
    let eyeBearing = snake.bearing
    if (snake.segments.length >= 2) {
      const dx = snake.segments[0].x - snake.segments[1].x
      const dz = snake.segments[0].z - snake.segments[1].z
      if (dx * dx + dz * dz > 1) eyeBearing = Math.atan2(dz, dx)
    }

    const cos = Math.cos(eyeBearing)
    const sin = Math.sin(eyeBearing)

    // Left eye
    const lx = snake.x + cos * eyeOffset * 0.5 - sin * eyeOffset
    const lz = snake.z + sin * eyeOffset * 0.5 + cos * eyeOffset
    gfx.circle(lx, lz, eyeSize)
    gfx.fill({ color: eyeColor })
    gfx.circle(lx + cos * eyeSize * 0.3, lz + sin * eyeSize * 0.3, eyeSize * 0.5)
    gfx.fill({ color: 0x000000 })

    // Right eye
    const rx = snake.x + cos * eyeOffset * 0.5 + sin * eyeOffset
    const rz = snake.z + sin * eyeOffset * 0.5 - cos * eyeOffset
    gfx.circle(rx, rz, eyeSize)
    gfx.fill({ color: eyeColor })
    gfx.circle(rx + cos * eyeSize * 0.3, rz + sin * eyeSize * 0.3, eyeSize * 0.5)
    gfx.fill({ color: 0x000000 })
  }

  /** Override color seed for a specific snake (e.g. match menu preview color). */
  setColorSeedOverride(snakeId: string, seed: number): void {
    this.colorSeedOverrides.set(snakeId, seed)
  }

  triggerPortalFade(snake: SnakeData): void {
    const id = snake.id
    const existing = this.portalFadeEffects.get(id)
    if (existing) {
      this.portalFadeLayer.removeChild(existing.container)
      existing.container.destroy({ children: true })
      this.portalFadeEffects.delete(id)
    }

    const colorSeed = this.colorSeedOverrides.get(id) ?? (Number(id) || 0)
    const container = new Container()
    const gfx = new Graphics()
    container.addChild(gfx)
    this.portalFadeLayer.addChild(container)
    this.portalFadeEffects.set(id, {
      effect: {
        id,
        timer: PORTAL_FADE_DURATION,
        maxTimer: PORTAL_FADE_DURATION,
        snake,
        colors: this.resolveSnakeColors(colorSeed),
      },
      container,
      gfx,
    })
  }

  triggerDeathGhost(snake: SnakeData): void {
    const id = snake.id
    const existing = this.deathGhostEffects.get(id)
    if (existing) {
      this.deathGhostLayer.removeChild(existing.container)
      existing.container.destroy({ children: true })
      this.deathGhostEffects.delete(id)
    }

    const colorSeed = this.colorSeedOverrides.get(id) ?? (Number(id) || 0)
    const container = new Container()
    const gfx = new Graphics()
    container.addChild(gfx)
    this.deathGhostLayer.addChild(container)
    this.deathGhostEffects.set(id, {
      effect: {
        id,
        timer: DEATH_GHOST_DURATION,
        maxTimer: DEATH_GHOST_DURATION,
        snake,
        colors: this.resolveSnakeColors(colorSeed),
      },
      container,
      gfx,
    })
  }

  setQualityLevel(level: number): void {
    if (!Number.isFinite(level)) return
    this.qualityLevel = Math.max(0, Math.min(3, Math.floor(level)))
  }

  setRenderMode(mode: GameMode): void {
    this.renderMode = mode
  }

  setBodyScale(scale: number): void {
    if (!Number.isFinite(scale)) return
    this.bodyScale = Math.max(0.8, Math.min(1.8, scale))
  }

  dispose(): void {
    for (const { container } of this.portalFadeEffects.values()) {
      container.destroy({ children: true })
    }
    this.portalFadeEffects.clear()
    for (const { container } of this.deathGhostEffects.values()) {
      container.destroy({ children: true })
    }
    this.deathGhostEffects.clear()
    this.container.removeChildren()
    this.portalFadeLayer.removeChildren()
    this.deathGhostLayer.removeChildren()
    this.container.parent?.removeChild(this.container)
    this.portalFadeLayer.parent?.removeChild(this.portalFadeLayer)
    this.deathGhostLayer.parent?.removeChild(this.deathGhostLayer)
    this.container.destroy()
    this.portalFadeLayer.destroy()
    this.deathGhostLayer.destroy()
    this.snakeContainers.clear()
    this.snakeGraphics.clear()
    this.detailState.clear()
    this.glowState.clear()
    this.colorSeedOverrides.clear()
    this.colorCache.clear()
    this.botColorStates.clear()
  }

  private resolveSnakeColors(colorKey: number): SnakeColors {
    const normalizedKey = (Math.round(colorKey) || 0) >>> 0
    let cached = this.colorCache.get(normalizedKey)
    if (!cached) {
      cached = generateSnakeColors(normalizedKey)
      this.colorCache.set(normalizedKey, cached)
    }
    return cached
  }

  private updatePortalFadeEffects(dt: number, now: number, viewport?: WorldViewport): void {
    for (const [id, entry] of this.portalFadeEffects) {
      entry.effect.timer -= dt
      if (entry.effect.timer <= 0) {
        this.portalFadeLayer.removeChild(entry.container)
        entry.container.destroy({ children: true })
        this.portalFadeEffects.delete(id)
        continue
      }

      if (viewport && !this.isSnakeVisibleInViewport(entry.effect.snake, viewport, EFFECT_CULL_MARGIN, 3)) {
        entry.container.visible = false
        continue
      }

      entry.container.visible = true
      this.renderPortalFade(entry.effect, entry.gfx, now)
    }
  }

  private updateDeathGhostEffects(dt: number, now: number, viewport?: WorldViewport): void {
    for (const [id, entry] of this.deathGhostEffects) {
      entry.effect.timer -= dt
      if (entry.effect.timer <= 0) {
        this.deathGhostLayer.removeChild(entry.container)
        entry.container.destroy({ children: true })
        this.deathGhostEffects.delete(id)
        continue
      }

      if (viewport && !this.isSnakeVisibleInViewport(entry.effect.snake, viewport, EFFECT_CULL_MARGIN, 3)) {
        entry.container.visible = false
        continue
      }

      entry.container.visible = true
      this.renderDeathGhost(entry.effect, entry.gfx, now)
    }
  }

  private drawTailTip(
    gfx: Graphics,
    snake: SnakeData,
    width: number,
    segmentSpacing: number,
    color: number,
    alpha: number,
  ): void {
    const segments = snake.segments
    const segCount = segments.length
    if (segCount < 1 || snake.tailProgress <= 0.001) return

    const tail = segments[segCount - 1]
    const beforeTail = segCount >= 2
      ? segments[segCount - 2]
      : {
          x: tail.x + Math.cos(snake.bearing + Math.PI) * segmentSpacing,
          z: tail.z + Math.sin(snake.bearing + Math.PI) * segmentSpacing,
        }
    const tailDx = tail.x - beforeTail.x
    const tailDz = tail.z - beforeTail.z
    const rawTailLen = Math.hypot(tailDx, tailDz)
    const dirX = rawTailLen > 0.001 ? tailDx / rawTailLen : Math.cos(snake.bearing + Math.PI)
    const dirZ = rawTailLen > 0.001 ? tailDz / rawTailLen : Math.sin(snake.bearing + Math.PI)
    const normalX = -dirZ
    const normalZ = dirX
    const tipDistance = segmentSpacing * (0.18 + snake.tailProgress * 0.82)
    const tipX = tail.x + dirX * tipDistance
    const tipZ = tail.z + dirZ * tipDistance
    const baseHalfWidth = width * (0.18 + 0.16 * snake.tailProgress)
    const baseCenterX = tail.x - dirX * width * 0.02
    const baseCenterZ = tail.z - dirZ * width * 0.02
    const leftX = baseCenterX + normalX * baseHalfWidth
    const leftZ = baseCenterZ + normalZ * baseHalfWidth
    const rightX = baseCenterX - normalX * baseHalfWidth
    const rightZ = baseCenterZ - normalZ * baseHalfWidth

    gfx.moveTo(tipX, tipZ)
    gfx.lineTo(leftX, leftZ)
    gfx.lineTo(rightX, rightZ)
    gfx.closePath()
    gfx.fill({ color, alpha })
  }

  private renderPortalFade(effect: PortalFadeEffect, gfx: Graphics, now: number): void {
    gfx.clear()
    const progress = 1 - effect.timer / effect.maxTimer
    const fadeAlpha = Math.pow(1 - progress, 0.7)
    const whiten = Math.min(1, progress * 1.15)
    const headColor = mixColors(effect.colors.head, 0xffffff, whiten)
    const bodyA = mixColors(effect.colors.body[0], 0xffffff, whiten)
    const bodyB = mixColors(effect.colors.body[1], 0xffffff, whiten)
    const segments = effect.snake.segments
    const width = effect.snake.width * this.bodyScale * (1 + progress * 0.22)
    const headSize = (this.renderMode === 'classic' ? width * 0.5 : width * 0.7) * (1 + progress * 0.16)

    if (segments.length >= 2) {
      gfx.moveTo(segments[0].x, segments[0].z)
      for (let i = 1; i < segments.length; i++) {
        gfx.lineTo(segments[i].x, segments[i].z)
      }
      gfx.stroke({
        color: mixColors(bodyA, bodyB, 0.5),
        alpha: fadeAlpha * 0.46,
        width: width * 0.82,
        cap: 'round',
        join: 'round',
      })
    }

    const glowAlpha = fadeAlpha * (0.14 + Math.sin(now * 0.01) * 0.03)
    for (let i = segments.length - 1; i >= 0; i--) {
      const seg = segments[i]
      const t = i / Math.max(1, segments.length - 1)
      const segWidth = this.renderMode === 'classic'
        ? width
        : width * (0.62 + 0.38 * (1 - t))
      const radius = segWidth / 2
      gfx.circle(seg.x, seg.z, radius * (1.25 + progress * 0.2))
      gfx.fill({ color: headColor, alpha: glowAlpha })
      gfx.circle(seg.x, seg.z, radius)
      gfx.fill({ color: i % 2 === 0 ? bodyA : bodyB, alpha: fadeAlpha * 0.82 })
    }

    gfx.circle(effect.snake.x, effect.snake.z, headSize * 1.4)
    gfx.fill({ color: 0xffffff, alpha: fadeAlpha * 0.28 })
    gfx.circle(effect.snake.x, effect.snake.z, headSize)
    gfx.fill({ color: headColor, alpha: fadeAlpha })
  }

  private renderDeathGhost(effect: DeathGhostEffect, gfx: Graphics, now: number): void {
    gfx.clear()
    const progress = 1 - effect.timer / effect.maxTimer
    const goldMix = progress < 0.25
      ? this.lerp(0, 0.3, progress / 0.25)
      : progress < 0.5
        ? this.lerp(0.3, 0.7, (progress - 0.25) / 0.25)
        : progress < 0.75
          ? this.lerp(0.7, 1, (progress - 0.5) / 0.25)
          : 1
    const fadeAlpha = progress < 0.25
      ? this.lerp(1, 0.9, progress / 0.25)
      : progress < 0.5
        ? this.lerp(0.9, 0.7, (progress - 0.25) / 0.25)
        : progress < 0.75
          ? this.lerp(0.7, 0.4, (progress - 0.5) / 0.25)
          : this.lerp(0.4, 0.04, (progress - 0.75) / 0.25)

    const goldHead = mixColors(0xFFE9A3, 0xFFFFFF, Math.min(1, progress * 1.1))
    const goldBodyA = 0xFFC938
    const goldBodyB = 0xFFEF9A
    const bodyA = mixColors(effect.colors.body[0], goldBodyA, goldMix)
    const bodyB = mixColors(effect.colors.body[1], goldBodyB, goldMix)
    const headColor = mixColors(effect.colors.head, goldHead, Math.min(1, goldMix * 1.1))
    const shimmer = 0.12 + Math.sin(now * 0.02) * 0.03
    const segments = effect.snake.segments
    const width = effect.snake.width * this.bodyScale * (1 + progress * 0.1)
    const headSize = (this.renderMode === 'classic' ? width * 0.5 : width * 0.7) * (1 + progress * 0.08)

    if (segments.length >= 2) {
      gfx.moveTo(segments[0].x, segments[0].z)
      for (let i = 1; i < segments.length; i++) {
        gfx.lineTo(segments[i].x, segments[i].z)
      }
      gfx.stroke({
        color: mixColors(bodyA, bodyB, 0.5),
        alpha: fadeAlpha * 0.54,
        width: width * 0.82,
        cap: 'round',
        join: 'round',
      })
    }

    for (let i = segments.length - 1; i >= 0; i--) {
      const seg = segments[i]
      const t = i / Math.max(1, segments.length - 1)
      const segWidth = this.renderMode === 'classic'
        ? width
        : width * (0.62 + 0.38 * (1 - t))
      const radius = segWidth / 2
      const bodyColor = i % 2 === 0 ? bodyA : bodyB
      gfx.circle(seg.x, seg.z, radius * (1.28 + progress * 0.16))
      gfx.fill({ color: goldHead, alpha: fadeAlpha * shimmer })
      gfx.circle(seg.x, seg.z, radius)
      gfx.fill({ color: bodyColor, alpha: fadeAlpha * 0.9 })
    }

    gfx.circle(effect.snake.x, effect.snake.z, headSize * 1.45)
    gfx.fill({ color: 0xFFF6C0, alpha: fadeAlpha * 0.24 })
    gfx.circle(effect.snake.x, effect.snake.z, headSize)
    gfx.fill({ color: headColor, alpha: fadeAlpha })
  }

  private lerp(a: number, b: number, t: number): number {
    const clamped = Math.max(0, Math.min(1, t))
    return a + (b - a) * clamped
  }

  getActiveGraphicsCount(): number {
    return this.snakeGraphics.size
  }

  getActivePortalFadeCount(): number {
    return this.portalFadeEffects.size
  }
}
